import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Blocks } from '../Blocks/Blocks';
import CLEs from '../../common/CLEs';
import {
  Form,
  FormSection,
  FormInput,
  FormInputValueTypes,
  FormWarning,
  Button,
  IWarning,
  IBlockContent,
  ICLECredit,
} from '@praktio/common-ui';

interface IProps {
  course?: ICourse;
  // TODO - Make this a type or interface
  // so that the arguments etc can be defined in a single place and
  // used throughout the forms.
  onChange?: ({
    fieldName,
    value,
  }: {
    fieldName?: string;
    value?: FormInputValueTypes | IBlockContent | ICLECredit;
  }) => void;
  setShowDeleteCourseModal?: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: any;
  warnings?: IWarning[];
  errors?: IBootstrapFormErrors;
  className?: string;
}

export const CourseSettings = ({
  course,
  onChange,
  setShowDeleteCourseModal,
  handleSubmit,
  warnings,
  errors,
  className,
}: IProps) => {
  let logoSource;
  if (course?.logo) {
    const logo = course.logo;
    logoSource = typeof logo === 'object' ? URL.createObjectURL(logo) : logo;
  }
  let collabSource;
  if (course?.collaboration) {
    const collabImage = course.collaboration;
    collabSource = typeof collabImage === 'object' ? URL.createObjectURL(collabImage) : collabImage;
  }

  return (
    <Form formId="course-settings-form" onSubmit={handleSubmit} className={`${className}`}>
      {warnings &&
        warnings.length > 0 &&
        warnings.map((warning, i) => <FormWarning warning={warning} index={i} />)}
      <FormSection sectionName="Course Information">
        <FormInput
          type="switch"
          fieldName="published"
          label={course?.published ? 'Published' : 'Unpublished'}
          value={course?.published}
          onChange={() =>
            onChange ? onChange({ fieldName: 'published', value: !course?.published }) : null
          }
        />
        <FormInput
          fieldName="courseName"
          label="Name"
          placeholder="Course Name"
          value={course?.title}
          onChange={(event: { target: any }) =>
            onChange ? onChange({ fieldName: 'title', value: event.target.value }) : null
          }
          error={errors?.title}
          singleLine
        />
        <FormInput
          type="textarea"
          fieldName="subtitle"
          label="Subtitle"
          value={course?.subtitle}
          onChange={(event: { target: any }) =>
            onChange ? onChange({ fieldName: 'subtitle', value: event.target.value }) : null
          }
        />
        <FormInput
          type="textarea"
          fieldName="description"
          label="Description"
          value={course?.description}
          onChange={(event: { target: any }) =>
            onChange ? onChange({ fieldName: 'description', value: event.target.value }) : null
          }
        />
        <FormInput
          fieldName="duration"
          type="number"
          label="Duration (in hours)"
          numMin={0}
          numStep={0.1}
          value={course?.duration}
          placeholder="Hours"
          onChange={(event: { target: any }) =>
            onChange ? onChange({ fieldName: 'duration', value: event.target.value }) : null
          }
          singleLine
          smallInput
        />
        <FormInput
          fieldName="logo"
          label="Course Icon"
          subtext="100 x 100px"
          value=""
          secondaryButton
          type="file"
          onChange={(event: { target: any }) =>
            onChange ? onChange({ fieldName: 'logo', value: event.target.files }) : null
          }
          fileSelectButtonText={logoSource ? 'Replace' : 'Upload'}
          singleLine
          fileSource={logoSource}
          filePreviewClassName="LogoPreview"
          lastInSection
        >
          {logoSource && (
            <Button
              onClick={() => (onChange ? onChange({ fieldName: 'logo', value: undefined }) : null)}
              className="secondary"
            >
              Clear
            </Button>
          )}
        </FormInput>
      </FormSection>

      <FormSection
        sectionName="Course Information Page"
        sectionDescription="Course information blocks for the Course Information Page"
      >
        <Blocks blocks={course?.blocks} onUpdateBlock={onChange} course={course} />
      </FormSection>

      <FormSection sectionName="CLE Information">
        <CLEs cles={course?.cleCredits} onAddUpdateCLE={onChange} creditsBlock />
        <FormInput
          fieldName="authoredBy"
          label="Author Information"
          type="textarea"
          value={course?.authoredBy}
          lastInSection
          placeholder="ex: Michael Bloom, an attorney currently in good standing and licensed in Michigan"
          subtext={`See the "CLE" entry in the "Couse Information Page" section above for a preview of how this text will appear in place.`}
          onChange={(event: any) =>
            onChange ? onChange({ fieldName: 'authoredBy', value: event.target.value }) : null
          }
        />
      </FormSection>

      <FormSection sectionName="Course Settings">
        <FormInput
          fieldName="modulesInOrder"
          type="radio"
          label="Module Completion"
          singleLine
          inlineChoices
          // Because we are using radio buttons to handle a boolean field
          // (which would normally be handled by a checkbox), we need to do
          // string<=>boolean conversion as radio inputs only allow strings.
          value={course?.modulesInOrder === true ? 'true' : 'false'}
          onChange={(event: { target: any }) =>
            onChange
              ? onChange({
                  fieldName: 'modulesInOrder',
                  value: event.target.value === 'true' ? true : false,
                })
              : null
          }
          radioChoices={[
            {
              label: 'In Order',
              value: 'true',
            },
            {
              label: 'Any Order',
              value: 'false',
            },
          ]}
        />
        <FormInput
          fieldName="certUponComplete"
          label="Certificate Upon Completion"
          type="switch"
          value={!!course?.certUponComplete}
          onChange={() =>
            onChange
              ? onChange({ fieldName: 'certUponComplete', value: !course?.certUponComplete })
              : null
          }
        />

        <FormInput
          fieldName="expirationSpan"
          label="Expiration Span (in days)"
          value={course?.expirationSpan}
          type="number"
          singleLine
          mediumInput
          placeholder="No expiration span set"
          numMin={1}
          onChange={(e: any) =>
            onChange ? onChange({ fieldName: 'expirationSpan', value: e.target.value }) : null
          }
        />
        <FormInput
          fieldName="collaboration"
          label="Collaboration Branding"
          subtext="Horizontal logo: 200 x 100px"
          value=""
          lastInSection
          type="file"
          onChange={(event: any) =>
            onChange ? onChange({ fieldName: 'collaboration', value: event.target.files }) : null
          }
          fileSelectButtonText={collabSource ? 'Replace' : 'Upload'}
          secondaryButton
          singleLine
          fileSource={collabSource}
          filePreviewClassName="CollabPreview"
        >
          {collabSource && (
            <Button
              onClick={() =>
                onChange ? onChange({ fieldName: 'collaboration', value: undefined }) : null
              }
              className="secondary"
            >
              Clear
            </Button>
          )}
        </FormInput>
      </FormSection>

      <FormSection sectionName="Delete Course" lastSection>
        <Row>
          <Col xs={12} lg={9}>
            <div className="CourseSettings-delete-message text-left pb-3">
              Permanently delete this course from Praktio. This cannot be undone.
            </div>
          </Col>
          <Col xs={12} lg={3}>
            <Button
              onClick={() => (setShowDeleteCourseModal ? setShowDeleteCourseModal(true) : null)}
              className="secondary"
            >
              DELETE
            </Button>
          </Col>
        </Row>
      </FormSection>
    </Form>
  );
};
